"use client";

import { ChevronsRight, Lock } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { PosterVideo } from "@/components/ui/poster-video";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";

/** `hold`: 2x while the finger stays down. `locked`: 2x after it lifts. */
export type SpeedBoost = "off" | "hold" | "locked";

/** Press this long without moving to start 2x (X's timing). */
const HOLD_MS = 280;
/** Movement before HOLD_MS that turns the press into a swipe instead. */
const TAP_SLOP_PX = 10;
/** While holding, slide this far down to lock 2x. */
const LOCK_DISTANCE_PX = 56;
const DISMISS_DISTANCE_PX = 120;
const DISMISS_VELOCITY = 0.5;

type ViewerVideoSlideProps = {
  index: number;
  src: string;
  hlsSrc?: string;
  posterSrc?: string | null;
  blurHash?: string | null;
  active: boolean;
  muted: boolean;
  boost: SpeedBoost;
  /** Stable: hands the shell this slide's player for its controls. */
  registerVideo: (index: number, video: HTMLVideoElement | null) => void;
  onBoostChange: (boost: SpeedBoost) => void;
  /** Autoplay with sound was refused; the shell shows sound as off. */
  onAutoplayMuted: () => void;
  onTap: () => void;
  onRequestClose: () => void;
};

type Timer = { current: ReturnType<typeof setTimeout> | null };

const clearTimer = (timer: Timer) => {
  if (timer.current) clearTimeout(timer.current);
  timer.current = null;
};

/** Written to the node: a React render per pointermove would stutter. */
const offsetFrame = (
  frame: HTMLElement | null,
  y: number,
  animate: boolean,
) => {
  if (!frame) return;
  frame.style.transition = animate
    ? "transform 260ms var(--ease-out-strong)"
    : "none";
  frame.style.transform = y ? `translate3d(0, ${y}px, 0)` : "";
};

type Gesture = {
  pointerId: number;
  startX: number;
  startY: number;
  startAt: number;
  mode: "pending" | "hold" | "dismiss" | "swipe";
  /** The lock already fired during this hold. */
  locked: boolean;
};

export function ViewerVideoSlide({
  index,
  src,
  hlsSrc,
  posterSrc,
  blurHash,
  active,
  muted,
  boost,
  registerVideo,
  onBoostChange,
  onAutoplayMuted,
  onTap,
  onRequestClose,
}: ViewerVideoSlideProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirrors `boost` for the pointer handlers. Written eagerly by `setBoost`
  // too: a release in the same frame as the lock must not read the old value.
  const boostRef = useRef(boost);
  useEffect(() => {
    boostRef.current = boost;
  }, [boost]);
  const setBoost = (next: SpeedBoost) => {
    boostRef.current = next;
    onBoostChange(next);
  };
  const [lockArmed, setLockArmed] = useState(false);

  const setVideo = useCallback(
    (node: HTMLVideoElement | null) => {
      videoRef.current = node;
      registerVideo(index, node);
    },
    [index, registerVideo],
  );

  // The active slide plays; a neighbour keeps its frame and position.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `active` is the only trigger; `muted` is applied below without restarting playback
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!active) {
      video.pause();
      return;
    }
    video.muted = muted;
    void video.play().catch(() => {
      if (video.muted) return;
      // Sound needs a gesture this open did not carry: start muted, like X.
      video.muted = true;
      onAutoplayMuted();
      void video.play().catch(() => {});
    });
  }, [active]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  const clearHoldTimer = () => clearTimer(holdTimerRef);
  const setFrameOffset = (y: number, animate: boolean) =>
    offsetFrame(frameRef.current, y, animate);

  /**
   * Embla reads raw touch/mouse events on the carousel viewport, an ancestor
   * of this surface. React's synthetic stopPropagation runs at the root, after
   * Embla has already seen them, so a hold or a dismiss pull stops them here,
   * natively, or the carousel slides sideways with the finger.
   */
  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;
    const shield = (event: Event) => {
      const mode = gestureRef.current?.mode;
      if (mode === "hold" || mode === "dismiss") event.stopPropagation();
    };
    surface.addEventListener("touchmove", shield, { passive: true });
    surface.addEventListener("mousemove", shield);
    return () => {
      surface.removeEventListener("touchmove", shield);
      surface.removeEventListener("mousemove", shield);
    };
  }, []);

  useEffect(() => () => clearTimer(holdTimerRef), []);

  // A swipe to another item ends any hold on this one.
  useEffect(() => {
    if (active) return;
    clearTimer(holdTimerRef);
    gestureRef.current = null;
    setLockArmed(false);
    offsetFrame(frameRef.current, 0, false);
  }, [active]);

  const startHold = () => {
    const gesture = gestureRef.current;
    if (gesture?.mode !== "pending") return;
    gesture.mode = "hold";
    uiHaptics.longPress();
    // Holding a locked video keeps it locked; lifting leaves it there.
    if (boostRef.current !== "locked") setBoost("hold");
    const video = videoRef.current;
    if (video?.paused) void video.play().catch(() => {});
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!event.isPrimary || (event.pointerType === "mouse" && event.button))
      return;
    event.currentTarget.setPointerCapture(event.pointerId);
    gestureRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startAt: performance.now(),
      mode: "pending",
      locked: false,
    };
    clearHoldTimer();
    holdTimerRef.current = setTimeout(startHold, HOLD_MS);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    const dx = event.clientX - gesture.startX;
    const dy = event.clientY - gesture.startY;

    if (gesture.mode === "pending") {
      if (Math.hypot(dx, dy) < TAP_SLOP_PX) return;
      clearHoldTimer();
      // Down and mostly vertical pulls the video away; sideways is Embla's.
      gesture.mode = dy > 0 && dy > Math.abs(dx) ? "dismiss" : "swipe";
    }

    if (gesture.mode === "dismiss") {
      setFrameOffset(Math.max(0, dy), false);
      return;
    }

    if (gesture.mode === "hold" && boostRef.current !== "locked") {
      const armed = dy >= LOCK_DISTANCE_PX;
      setLockArmed(armed);
      if (armed && !gesture.locked) {
        gesture.locked = true;
        uiHaptics.success();
        setBoost("locked");
      }
    }
  };

  const finishGesture = (
    event: React.PointerEvent<HTMLDivElement>,
    cancelled: boolean,
  ) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    gestureRef.current = null;
    clearHoldTimer();
    setLockArmed(false);

    if (gesture.mode === "hold") {
      if (boostRef.current === "hold") setBoost("off");
      return;
    }
    if (gesture.mode === "dismiss") {
      const dy = Math.max(0, event.clientY - gesture.startY);
      const velocity = dy / Math.max(1, performance.now() - gesture.startAt);
      if (
        !cancelled &&
        (dy > DISMISS_DISTANCE_PX || (dy > 48 && velocity > DISMISS_VELOCITY))
      ) {
        onRequestClose();
      } else {
        setFrameOffset(0, true);
      }
      return;
    }
    if (gesture.mode === "pending" && !cancelled) onTap();
  };

  const isBoosted = active && boost !== "off";

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div
        ref={frameRef}
        className="flex h-full w-full items-center justify-center"
      >
        {/* A full-size frame and a poster cover: the box never depends on
            metadata, and iOS never shows black while the source attaches. */}
        <PosterVideo
          ref={setVideo}
          src={src}
          hlsSrc={hlsSrc}
          posterUrl={posterSrc}
          blurHash={blurHash}
          posterSizes="100vw"
          posterClassName="object-contain"
          coverClassName="bg-black bg-none!"
          className="h-full w-full"
          videoClassName="object-contain"
          playsInline
          preload={active ? "auto" : "metadata"}
        />
      </div>

      {/* The gesture surface sits over the player so a long press never
          reaches WebKit's own media callout. `touch-none` keeps the browser
          from claiming a vertical slide (a pointercancel mid-hold). */}
      <div
        ref={surfaceRef}
        aria-hidden="true"
        className="absolute inset-0 touch-none select-none [-webkit-touch-callout:none]"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={(event) => finishGesture(event, false)}
        onPointerCancel={(event) => finishGesture(event, true)}
        onContextMenu={(event) => event.preventDefault()}
      />

      <div
        className={cn(
          "pointer-events-none absolute inset-x-0 top-[calc(env(safe-area-inset-top)+4.25rem)] z-20 flex flex-col items-center gap-2 transition-[opacity,transform] duration-200 ease-out-strong",
          isBoosted ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0",
        )}
      >
        {boost === "locked" ? (
          <button
            type="button"
            onClick={() => {
              uiHaptics.select();
              setBoost("off");
            }}
            className="pointer-events-auto flex h-8 items-center gap-1.5 rounded-full bg-black/55 px-3 text-[13px] font-semibold text-white tabular-nums backdrop-blur-md backdrop-saturate-150"
            aria-label="2x speed locked. Tap to return to normal speed"
          >
            <Lock className="size-3.5" aria-hidden="true" />
            2x
            <ChevronsRight className="size-4" aria-hidden="true" />
          </button>
        ) : (
          <span className="flex h-8 items-center gap-1 rounded-full bg-black/55 px-3 text-[13px] font-semibold text-white tabular-nums backdrop-blur-md backdrop-saturate-150">
            2x
            <ChevronsRight className="size-4" aria-hidden="true" />
          </span>
        )}
        {boost === "hold" ? (
          <span
            className={cn(
              "flex items-center gap-1 text-[12px] font-medium text-white/80 transition-colors duration-150 [text-shadow:0_1px_2px_rgba(0,0,0,0.6)]",
              lockArmed && "text-white",
            )}
          >
            <Lock className="size-3" aria-hidden="true" />
            Slide down to lock
          </span>
        ) : null}
      </div>
    </div>
  );
}
